import {
  createFileRoute,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import { AnimatePresence, motion, useIsPresent } from "framer-motion";

export const Route = createFileRoute("/_app/_recipe-page")({
  component: RecipePageLayout,
});

const NEW_RECIPE_ROUTE_ID = "/_app/_recipe-page/recipes/new";

// See the matching comment in _app.tsx: <Outlet/> keeps reflecting the live
// route match even while its ancestor is being kept mounted by
// AnimatePresence for an exit animation, so it must be hidden (not just
// left to animate) once it's no longer the current match.
function StableOutlet({ children }: { children: React.ReactNode }) {
  const isPresent = useIsPresent();
  return <div className={isPresent ? "contents" : "invisible"}>{children}</div>;
}

function RecipePageLayout() {
  const leafRouteId = useRouterState({
    select: (s) => s.matches.at(-1)?.routeId ?? "",
  });

  // The New Recipe page's container transition (FAB layoutId morph) is
  // owned by _app.tsx's outer AnimatePresence. Its content fades in with a
  // short delay so form fields don't appear squeezed into the FAB's small
  // starting shape mid-morph.
  if (leafRouteId === NEW_RECIPE_ROUTE_ID) {
    return (
      <div className="flex h-full flex-col">
        <motion.div
          className="flex h-full flex-col"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.2 }}
        >
          <Outlet />
        </motion.div>
      </div>
    );
  }

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={leafRouteId}
        className="flex h-full flex-col"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
      >
        <StableOutlet>
          <Outlet />
        </StableOutlet>
      </motion.div>
    </AnimatePresence>
  );
}
